import { documentToHtmlString as htmlToString } from '@contentful/rich-text-html-renderer'
import presetV2 from '~~/tailwind.preset.dsv2.js'
import sha256 from 'crypto-js/sha256'
import parse from 'html-dom-parser'
import dayjs from 'dayjs'
import _isEmpty from 'underscore/cjs/isEmpty.js'
import _isObject from 'underscore/cjs/isObject.js'
import _isArray from 'underscore/cjs/isArray.js'
import memberhubConstants from '~/constants/memberhub.constants'
import { Capacitor } from '@capacitor/core'
import { InAppBrowser, DefaultSystemBrowserOptions } from '@capacitor/inappbrowser'

import duration from 'dayjs/plugin/duration.js'
import customParseFormat from 'dayjs/plugin/customParseFormat.js'

dayjs.extend(duration)
dayjs.extend(customParseFormat)

export const documentToHtmlString = doc => htmlToString(doc)

export const strToCapitalize = str => {
  try {
    if (!str) {
      return str
    }

    return str.replace(/\b\w/g, l => l.toUpperCase())
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error)
  }
}

/*
 * transforms string to Kebab Case
 * @param str {String}
 */
export const strToKebabCase = str => {
  if (!str) {
    return str
  }

  return str.match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
    .map(x => x.toLowerCase())
    .join('-')
}

/*
 * transforms string to Snake Case
 * @param str {String}
 */
export const strToSnakeCase = str => {
  if (!str) {
    return str
  }

  return str
    .replace(/\s/g, '_')
    .replace(/[^a-z|0-9]/gi, '_')
    .replace(/\B([A-Z])/g, '_$1')
    .toLowerCase()
    .replace(/__/, '_')
}
/*
 * transforms string to Camel Case
 * @param str {String}
 */
export const strToCamelCase = str => {
  if (!str) {
    return str
  }

  return `${str}`.toLowerCase()
    .replace(/[^a-zA-Z0-9]+(.)/g, (match, val) => val.toUpperCase())
}

export const strToPascalCase = str => {
  if (!str) {
    return str
  }

  return str.toLowerCase()
    .replace(new RegExp(/[-_]+/, 'g'), ' ')
    .replace(new RegExp(/[^\w\s]/, 'g'), '')
    .replace(
      new RegExp(/\s+(.)(\w*)/, 'g'),
      ($1, $2, $3) => `${$2.toUpperCase() + $3}`
    )
    .replace(new RegExp(/\w/), s => s.toUpperCase())
}

export const strToCapitalizeWords = str => {
  if (!str) {
    return str
  }

  return str.toLowerCase()
    .replace(new RegExp(/[-_]+/, 'g'), ' ')
    .replace(new RegExp(/[^\w\s]/, 'g'), '')
    .replace(
      new RegExp(/\s+(.)(\w*)/, 'g'),
      ($1, $2, $3) => ` ${$2.toUpperCase() + $3}`
    )
    .replace(new RegExp(/\w/), s => s.toUpperCase())
}

/*
 * Get an item in localstorage
 * @param key
 * @param val
 */
export const getLocal = key => {
  if (import.meta.client && typeof localStorage !== 'undefined') {
    return localStorage.getItem(key)
  }
  return null
}

/*
 * Set an item in localstorage
 * @param key
 * @param val
 */
export const setLocal = (key, val) => {
  if (import.meta.client && typeof localStorage !== 'undefined') {
    localStorage.setItem(key, val)
  }
}

/*
 * Remove an item from local storage
 * @param key
 */
export const removeLocal = key => {
  if (import.meta.client && typeof localStorage !== 'undefined') {
    localStorage.removeItem(key)
  }
}

/*
 * Dispatching custom event to DOM
 * @param eventName
 * @param payload
 */
export const dispatchEvent = (eventName, payload) => {
  if (import.meta.client) {
    window.document.getElementById('app-main')?.dispatchEvent(new CustomEvent(eventName, { detail: payload }))
  }
}

/*
 * generates a unique id
 * @param str {String}
 */
export const generateUID = str => (`${(Math.random(Date.now() * Math.random(100 + 50))).toString(32)}${Math.random().toString(16)}${str ? `-${strToKebabCase(str)}` : ''}`).replace(/\./g, '')

/**
 * Returns the value of key in the map
 * if the key doesn't exist it returns "default" key value or undefined
 * @param key {String}
 * @param obj {Object}
 */
export const getKey = (key, obj) => {
  const tempKey = Object.keys(obj).find(item => {
    if (item.includes(',')) {
      const tempItem = (item.includes(',') && item.split(',')
        .map(e => e.trim())) || ''
      return tempItem.includes(key)
    }

    return item === key
  }) ?? key

  return (tempKey in obj ? obj[tempKey] : (obj?.default || ''))
}

/**
 * Returns the key where the value of the map matches the
 * param `value` passed.
 * If the none of the value matches it will return null
 *
 * @param value {String}
 * @param obj {Object}
 */
export const getKeyOfMap = (value = true, obj, _default = null) => {
  const values = Object.values(obj)
  const keys = Object.keys(obj)
  const findIndex = values.findIndex(item => item === value)

  return keys?.[findIndex] ?? _default
}

/*
 * scroll to id handler
 * @param id {String}
 * @param offset {Number}
 * @returns promise
 */
export const scrollToId = (element, offset = 0) => {
  const { top } = element.getBoundingClientRect()

  const html = import.meta.client && document.documentElement
  const windowOffsetTop = window.scrollY || html.scrollTop
  const targetPosition = Math.min(top + windowOffsetTop - offset, document.documentElement.scrollHeight - window.innerHeight)

  window.scrollTo({
    top: targetPosition,
    behavior: 'smooth'
  })

  return new Promise(resolve => {
    const failed = setTimeout(() => {
      // eslint-disable-next-line no-console
      console.log('targetPosition not match with scrollY')
      resolve()
    }, 2000)

    const scrollHandler = () => {
      if (self.scrollY === targetPosition) {
        window.removeEventListener('scroll', scrollHandler)
        clearTimeout(failed)
        resolve()
      }
    }

    if (Math.abs(self.scrollY - targetPosition) <= 1) {
      clearTimeout(failed)
      resolve()
    } else {
      window.addEventListener('scroll', scrollHandler)
    }
  })
}

/*
 * check whether string contains html tag
 * @param str {String}
 * @returns {true|false}
 */
export const checkStringContainsHtml = str => {
  if (!str) {
    return str
  }

  return /<\/?[a-z][\s\S]*>/i.test(str)
}

/*
 * check whether elemt on viewport
 * @param element {HTML}
 * @returns Boolean
 */
export const isInViewport = element => {
  const SCROLL_TRIGGER = 80
  if (!element) {
    return false
  }
  const rect = element.getBoundingClientRect()

  return (
    rect.bottom - SCROLL_TRIGGER >= 0 &&
    rect.right >= 0 &&
    rect.top - SCROLL_TRIGGER <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.left <= (window.innerWidth || document.documentElement.clientWidth)
  )
}

/**
 * Removes top-level keys whose value is "empty" (0, false, null, undefined,
 * '', {}, []) from an object, returning a new object. Values are checked
 * with underscore's `_isEmpty`, unless `ignoreNullish` is true (see below).
 *
 * Note: the object is cloned via JSON.stringify/parse, so functions,
 * `undefined` values, and other non-JSON-safe values won't survive on
 * the returned object even if their key isn't removed.
 *
 * @param obj (required) {Object}
 * @param innerKey {String} - if the object's values are themselves objects,
 *   check `obj[key][innerKey]` instead of `obj[key]` when deciding whether
 *   to remove `key`.
 * @param ignoreNullish {Boolean} - when true, only removes keys whose value
 *   is `null`/`undefined` (via `isNullish`), keeping falsey-but-not-nullish
 *   values like `0`, `''`, and `false`.
 *
 * @example
 * removeFalseyObj({ a: 1, b: 0, c: '', d: false, e: 'ok' })
 * // => { a: 1, e: 'ok' }
 *
 * @example
 * // ignoreNullish keeps 0/''/false, only strips null/undefined
 * removeFalseyObj({ a: false, b: null, c: 0, d: undefined }, null, true)
 * // => { a: false, c: 0 }
 *
 * @example
 * // innerKey checks a nested property to decide whether to drop the key
 * removeFalseyObj({
 *   email: { value: '', valid: false },
 *   name: { value: 'Jane', valid: true }
 * }, 'value')
 * // => { name: { value: 'Jane', valid: true } }
 */
export const removeFalseyObj = (obj, innerKey = null, ignoreNullish = false) => {
  const temp = JSON.parse(JSON.stringify(obj))

  for (const key in temp) {
    if (Object.hasOwnProperty.call(temp, key)) {
      const item = _isEmpty(innerKey)
        ? temp[key]
        : temp[key]?.[innerKey]

      const check = !ignoreNullish
        ? _isEmpty(item)
        : isNullish(item)

      if (check) {
        delete temp[key]
      }
    }
  }

  return temp
}

/*
 * generate excerpt
 * @param str {String}
 * @param maxLength {Int}
 * @returns Boolean
 */
export const generateExcerpt = (str, maxLength = 120) => {
  return (str && str.substring(0, maxLength) + (str.length > maxLength ? '...' : '')) || str
}

/*
 * remove html from string
 * @param str {String}
 * @returns String
 */
export const removeHtmlString = str => {
  try {
    return (str && str.replace(/<(.|\n)*?>/g, '')
      .replace(/&amp;/g, '&')) || str
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(str, `=>  ${error}`)
    return str
  }
}

/**
 * transform Object into query url
 * @param Obj {Object}
 * @returns String
 */
export const transformObjectToQueryUrl = Obj => {
  return Object.keys(Obj).map(key => key + '=' + Obj[key]).join('&')
}

/**
 * generates a SHA256 hash
 * @param text {String}
 * @returns String
 */
export const hash256 = text => {
  if (!text) {
    return
  }

  text = `${text}`.toLowerCase()
  return sha256(text).toString()
}

/**
 * generates a validation result
 * @param Object
 * @returns Object
 */
export const generateValidation = ({ type = '', message = '' }) => {
  if (!['error', 'success'].includes(type)) {
    // eslint-disable-next-line no-console
    return console.error('type doesn\'t exist')
  }

  if (type === 'error') {
    return {
      success: { status: false },
      error: {
        status: true,
        message
      }
    }
  }

  return {
    success: { status: true },
    error: {
      status: false,
      message
    }
  }
}

/* flatten objects, and deep objects
 * @param obj {Object}
 * @param depth {String|Number}
 * @param parent {String}
 * @param newObj {Object}
 * @returns Object
 */
export const flattenObj = (object, depth = 'deep', parent, newObj = {}) => {
  if (_isEmpty(object)) {
    return {}
  }

  for (const key in JSON.parse(JSON.stringify(object))) {
    if (Object.hasOwnProperty.call(object, key)) {
      const item = object[key]
      const newKey = Object.keys(newObj).includes(key)
        ? `${parent}_${key}`
        : key

      if (typeof item === 'object' && !Array.isArray(item)) {
        if (depth > 0 || depth === 'deep') {
          const tempDepth = depth === 'deep'
            ? depth
            : depth - 1

          flattenObj(item, tempDepth, newKey, newObj)
        } else {
          newObj[newKey] = item
        }
      } else {
        newObj[newKey] = item
      }
    }
  }

  return newObj
}

/**
 * returns top and left positions of elements
 * @param el {HTMLElement}
 * @return obj {Object}
 */
export const getElOffset = el => {
  const rect = el && el.getBoundingClientRect()

  return {
    top: rect?.top + window?.scrollY,
    left: rect?.left + window?.scrollX,
    width: el?.offsetWidth,
    height: el?.offsetHeight
  }
}

/**
 * returns url with trailing slash
 * @param value {String}
 * @return value {String}
 */
export const trailingSlash = value => {
  if (!value) {
    return value
  }

  value = value.toString()

  if (value.charAt(value.length - 1) !== '/') {
    value = value + '/'
  }

  return value
}

/**
 * returns true/false if payload is object
 * @param payload
 * @return {Boolean}
 */
export const isObject = payload => !_isArray(payload) && _isObject(payload)

/**
 * returns true/false if payload is array
 * @param payload
 * @return {Boolean}
 */
export const isArray = payload => _isArray(payload) && _isObject(payload)

/**
 * return true/false if the paylod is nullish
 *
 * null, undefined (falsy & nullish) = true
 * ---
 * empty string (''), 0 (falsy but NOT nullish) = false
 * false (boolean) = false
 */
export const isNullish = payload => {
  return payload === null ||
    payload === undefined
}

/**
 * waits until timeout is resolved
 * @param time {Number}
 * example:
 *  await wait(300)
 */
export const wait = time => new Promise(resolve => setTimeout(resolve, time))

/**
 * returns element when its loaded in the window
 * @param elSelector {String}
 * @param all {Boolean}
 * example:
 *  (1) const el = await getElement('.test')
 *  (2) const el = await getElement('.test', true)
 */
export const getElement = async (elSelector, all = false) => {
  const selector = all ? 'querySelectorAll' : 'querySelector'

  while (!document[selector](elSelector)) {
    // eslint-disable-next-line no-await-in-loop
    await new Promise(resolve => requestAnimationFrame(resolve))
  }

  return document[selector](elSelector)
}

export const formatAmount = data => {
  if (
    data === null ||
    data === '' ||
    data === undefined
  ) {
    return ''
  }

  const number = data.toString()
    .replace(/[,]/g, '')
    .split('.')
  number[0] = number[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  return number.join('.')
}

/**
 * returns class without extra spacecs, and false
 * @param str {String}
 * @return {String}
 */
export const removeClassExtras = str => str && str.replace(/false/g, ' ').replace(/\s\s+/g, ' ')

/**
 * checks this.$route is sales funnel
 * @param route {String}
 * @return {Boolean}
 */
export const pageIsFunnel = route => {
  const path = route?.path
  const params = route?.params
  const step = params?.step

  // clubplus landing pages
  if (/^\/dashboard\/(clubplus|app)\/?$/.test(path)) return false

  const excludedQuotes = path?.includes('quote') &&
    (
      (path.includes('income-protection/quote') && !path.includes('income-protection/quote/details')) ||
      path.includes('visitors') ||
      path?.includes('results') ||
      path?.includes('resume') ||
      path?.includes('customer') ||
      path?.includes('home-loans/quote/ab-thank-you')
    )

  return !!(
    excludedQuotes ||
    path?.includes('form') ||
    (path.includes('aip/quote')) ||
    (path.includes('aip/thank-you')) ||
    step ||
    (path && path.match(/\/step/gi)) ||
    (path && path.match(/\/details\/$/)) ||
    (path && path.match(/\/health-insurance\/post-sale\/$/)) ||
    (path && path.match(/^\/dashboard(?:\/|$)/)) ||
    (path && path.match(/^\/login(?:\/|$)/)) ||
    (path && path.match(/^\/register(?:\/|$)/)) ||
    (path && path.match(/^\/guest(?:\/|$)/)) ||
    (path && path.match(/^\/reset-password(?:\/|$)/)) ||
    (path && path.match(/^\/link-expired(?:\/|$)/)) ||
    (path && path.match(/^\/forgot-password(?:\/|$)/)) ||
    (path && path.match(/^\/authorization(?:\/|$)/)) ||
    (path && path.match(/^\/verify(?:\/|$)/))
  )
}

export const htmlParser = str => {
  if (!str) {
    return str
  }

  return parse(str)
}

/**
 * Set an item in sessionStorage
 * @param key
 */
export const getSession = key => {
  if (import.meta.client && typeof sessionStorage !== 'undefined') {
    return sessionStorage.getItem(key)
  }

  return null
}

/**
 * Set an item in sessionStorage
 * @param key
 * @param val
 */
export const setSession = (key, val) => {
  if (import.meta.client && typeof sessionStorage !== 'undefined') {
    return sessionStorage.setItem(key, val)
  }

  return null
}

/*
 * Remove an item from local storage
 * @param key
 */
export const removeSession = key => {
  if (import.meta.client && typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(key)
  }
}

/**
 * handles parsing string to Object/Array
 * @param str {string}
 * @param _default {string} | {Object} | {Array}
 * @param showError {boolean}
 *
 * @return {Object} | {Array}
 * returns parsed string or default value
 */
export const parsify = (str, _default, showError = true) => {
  if ([null, undefined, 'null', 'undefined', ''].includes(str)) {
    return _default
  }

  const constructorName = str.constructor.name
  if (constructorName === 'Array' || constructorName === 'Object') {
    return JSON.parse(JSON.stringify(str))
  }

  try {
    return JSON.parse(str)
  } catch {
    // eslint-disable-next-line no-console
    showError && console.log(new Error(`Invalid JSON Format. Received ${str}`).stack)
    return _default
  }
}

export const getImageUrl = path => new URL(`${path}`, import.meta.url).href

export const currentHostURL = () => {
  const url = useRequestURL()
  return `${url.protocol}//${url.host}`
}

/*
 * Duration calc by dayjs
 * @param fromDate
 * @param toDate
 * @returns {{months: number, days: number, years: number}}
 */
export const renderDuration = (fromDate, toDate) => {
  const date1 = dayjs(fromDate, 'DD/MM/YYYY')
  const date2 = dayjs(toDate, 'DD/MM/YYYY')

  // get the difference between the moments
  const diff = date2.diff(date1)

  // express as a duration
  const dur = dayjs.duration(diff)
  return {
    years: dur.years(),
    months: dur.months(),
    days: dur.days()
  }
}

/*
 * Prepend currency if not present
 * @param amount
 * @returns {*|string}
 */
export const prependCurrency = amount => {
  if (amount && amount[0] !== '$') {
    return `$${amount}`
  }
  return amount
}

/*
 * Prepend currency if not present & format the amount
 * @param amount
 * @returns {*|string}
 */
export const formatCurrency = amount => {
  const tempAmount = amount?.toString()?.startsWith('$') ? amount?.substring(1)?.replace(/,/g, '') : (amount?.toString()?.replace(/,/g, ''))
  if (tempAmount && !tempAmount.startsWith('$')) {
    return `$${formatAmount((Math.round(parseFloat(tempAmount) * 100) / 100).toFixed(2).replace(/\.00$/, ''))}`
  }
  return amount
}

/*
 * Append percentage if not present
 * @param amount
 * @returns {*|string}
 */
export const appendPercentage = amount => {
  if (!amount?.endsWith('%')) {
    return `${amount}%`
  }
  return amount
}

/**
 * Returns list of arrays as {String} with separator
 * @param list {Array}
 * @param separator {String}
 * @return {String}
 */
export const joinArr = (list, separator = ',') => _isEmpty(list) ? '' : list.join(separator)

/**
 * Returns full_name, first_name, and last_name
 * @param str {String}
 * @return {Object}
 */
export const getName = str => {
  if (!str) {
    return ''
  }

  const name = str.split(' ')

  return {
    name: name.join(' '),
    first_name: name?.[0] ?? '',
    last_name: name?.[name.length - 1] ?? ''
  }
}

/**
 * Returns 24 hr time equivalent to 12 hr time given
 * NOTE:
 *  `time` should have AM or PM (i.e: `10:00 AM` `10:00am`)
 *  `format` is either full (default), hr, min, or sec
 *
 * @param time {String}
 * @param format {String}
 * @return {String}
 */
export const to24Hr = (time, format = 'full') => {
  time = time
    .toString()
    .toUpperCase()
    .replace(/ /g, '')

  if (!(time.includes('PM') || time.includes('AM'))) {
    // eslint-disable-next-line no-console
    console.log(`%cto24Hr: ${time} should either have AM or PM`, 'color: red')

    return null
  }

  if (!['full', 'hr', 'min', 'sec', 'hr-min'].includes(format)) {
    // eslint-disable-next-line no-console
    console.log(`%cto24Hr: ${format} should either be 'full', 'hr', 'min', 'sec'`, 'color: red')

    return null
  }

  const temp = time.replace(/PM|AM/, ':00')
    .split(':')

  if (time.includes('AM') && time.includes(12)) {
    temp[0] = '00'
  } else if (time.includes('PM')) {
    temp[0] = +temp[0] >= 1 && +temp[0] <= 11
      ? +temp[0] + 12
      : temp[0]
  }

  if (format === 'hr-min') {
    return `${temp[0]}:${temp[1]}`
  }

  if (format === 'hr') {
    return temp[0]
  }

  if (format === 'min') {
    return temp[1]
  }

  if (format === 'sec') {
    return temp[2]
  }

  return temp.join(':')
}

/*
 * File size rendering
 * @param size
 * @returns {string}
 */
export const fileSize = size => {
  if (size >= 1024) {
    size = size / 1024
    return size >= 1024 ? `${(size / 1024).toFixed(2)} MB` : `${size.toFixed(2)} KB`
  }
  return `${size} B`
}

const accessProperties = (object, string) => {
  const explodedString = string.split('.')
  for (let i = 0, l = explodedString.length; i<l; i++){
    object = object[explodedString[i]]
  }
  return object
}

/*
 * Return the Hex color code of the semantic color
 * @param colorName
 * @returns {string}
 */
export const semanticColorLookup = colorName => {
  const colors = presetV2.theme.extend.colors.semantic
  return accessProperties(colors, colorName) ?? ''
}

export const colorLookup = (colorName, preset = 'semantic') => {
  const colors = presetV2.theme.extend.colors[preset]
  return accessProperties(colors, colorName) ?? ''
}

/**
 * Refreshes the token inside the WKWebView via the user store and appends
 * the fresh access_token as a URL hash fragment for internal URLs opened
 * in the system browser.
 * Hash fragments are not sent to the server or included in Referrer headers.
 */
async function appendAuthToInternalUrl (url) {
  try {
    const internalDomain = useRuntimeConfig().public.internalDomainForInlinesHyperlink
    if (!internalDomain || !url.startsWith(internalDomain)) {
      return url
    }

    // On iOS/Android the page opens in the system browser (SFSafariViewController /
    // Chrome Custom Tabs), outside the Capacitor webview. Tag internal pages with
    // origin=<platform> so their "Back to dashboard" CTAs can deep-link back into the
    // native app (compareclub://dashboard) instead of navigating within the browser.
    // Tagging here covers every in-app-browser entry point centrally rather than
    // per-caller. Skip if an origin is already present so callers that set it
    // themselves aren't doubled.
    let taggedUrl = url
    const platform = Capacitor.getPlatform()
    if ((platform === 'ios' || platform === 'android') && !/[?&]origin=/.test(taggedUrl)) {
      taggedUrl += (taggedUrl.includes('?') ? '&' : '?') + `origin=${platform}`
    }

    const tokenRaw = getLocal('token')
    if (!tokenRaw) return taggedUrl

    const userStore = useUserStore()
    const freshAccessToken = await userStore.refreshToken()
    if (!freshAccessToken) return taggedUrl

    const encoded = encodeURIComponent(freshAccessToken)
    return `${taggedUrl}#cc_auth=${encoded}`
  } catch {
    return url
  }
}

/*
   * Opens a URL in a new browser tab.
   *
   * @param {string} url - The URL to open.
   * @param {string} [target='_blank'] - The target where to open the URL. Defaults to '_blank'.
   */
export const openInBrowser = async (url, target = '_blank') => {
  if (import.meta.client) {
    try {
      if (Capacitor.isNativePlatform()) {
        if(!/^https?:\/\//i.test(url)){
          const $router = useRouter()
          // internal link handling
          $router.push(url)
          return
        }

        const authUrl = await appendAuthToInternalUrl(url)
        InAppBrowser.openInSystemBrowser({
          url: authUrl,
          options: DefaultSystemBrowserOptions
        })
      } else {
        window.open(url, target)
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to open URL, retrying...', error)
      setTimeout(() => window.open(url, target))
    }
  }
}

/*
 * Navigates the user back to the dashboard, handling the iOS/Android in-app-browser case.
 *
 * Pages opened in the native in-app system browser (via openInBrowser's Capacitor-native
 * branch — SFSafariViewController on iOS, Chrome Custom Tabs on Android) are tagged with
 * ?origin=ios / ?origin=android by appendAuthToInternalUrl. On such a page a normal SPA
 * navigation would only move within the in-app browser and strand the user in the web view,
 * so instead we fire a compareclub:// deep link. The native app intercepts it
 * (capacitor-redirect.client.ts), closes the in-app browser and routes itself to /dashboard/.
 *
 * The URL scheme is environment-specific and shared across platforms: the production build
 * registers compareclub:// while dev registers compareclub-dev:// (iOS Info.plist / Android
 * manifest). The page is served from the env-specific domain, so the runtime environment
 * picks the right one.
 *
 * In every other context (plain web, PWA, or the Capacitor webview itself) it falls back to a
 * standard SPA navigation to `fallbackPath`.
 *
 * Use this only on pages that are opened directly from the dashboard via openInBrowser
 * (the entry URL carries origin=ios/android). Deeper in-funnel pages don't carry the marker.
 *
 * @param {string} [fallbackPath='/dashboard/'] - Route used for the non-app fallback.
 */
export const returnToAppDashboard = (fallbackPath = '/dashboard/') => {
  if (!import.meta.client) return

  // origin=ios / origin=android is set on in-app-browser entry URLs by
  // appendAuthToInternalUrl. The deep-link scheme is shared across platforms and
  // depends only on env: production = compareclub://, dev = compareclub-dev://.
  const origin = useRoute()?.query?.origin
  const isProd = useRuntimeConfig().public.environment === 'production'
  const scheme = isProd ? 'compareclub' : 'compareclub-dev'

  if (origin === 'ios') {
    // iOS (SFSafariViewController) launches custom schemes via window.location.
    window.location.href = `${scheme}://dashboard`
    return
  }

  if (origin === 'android') {
    // Android Chrome Custom Tabs BLOCK a JS window.location assignment to a custom
    // scheme, but they DO follow a top-level HTTP redirect into one (this is how the
    // SSO/OAuth callback returns to the app). So navigate to a same-origin endpoint that
    // 302-redirects to the scheme. The same Custom Tab follows the redirect, so the
    // InAppBrowser plugin keeps its reference and capacitor-redirect can close it.
    window.location.href = '/api/app/return-to-dashboard'
    return
  }

  useRouter().push(fallbackPath)
}

/*
 * Determines whether an energy vault payload is for a Gas product.
 * Prefers the numeric `energy_product_type` flag (always populated) over the
 * `product_type` string (frequently missing for Electricity records), so callers
 * don't each reinvent this fallback with inconsistent reliability.
 *
 * @param {object} payload - Vault product payload (e.g. vaultStore.getProduct.payload).
 * @returns {boolean}
 */
export const isEnergyGasProduct = payload => {
  if (!payload) return false
  if (payload.energy_product_type != null) {
    return payload.energy_product_type === memberhubConstants.ENERGY_PRODUCT_TYPE_FLAG.GAS
  }
  return String(payload.product_type || '').toLowerCase() === 'gas'
}

/*
 * Checks if the Progressive Web App (PWA) is installed.
 *
 * @returns {boolean} - Returns true if the PWA is installed, otherwise false.
 */
export const isPWAInstalled = () => {
  const { $pwa, $appDeviceInfo } = useNuxtApp()
  const displayMode = $appDeviceInfo?.displayMode
  return $pwa?.isPWAInstalled || displayMode === 'twa' || displayMode === 'ios' || displayMode === 'android'
}

/*
 * Height of the app's bottom nav bar (#pwa-nav-bar in layouts/memberhub.vue) as a
 * CSS length, or '0px' when the page shows none. The layout renders it only on the
 * dashboard tab pages, so full-page modals measure it rather than assume it from
 * isPWAInstalled() (true on every page of the app); the measured height also
 * covers its "No connection" bar and the safe-area inset.
 *
 * @returns {string}
 */
export const pwaNavBarOffset = () => {
  if (!import.meta.client) return '0px'
  const height = document.getElementById('pwa-nav-bar')?.getBoundingClientRect().height || 0
  return `${Math.round(height)}px`
}

/*
 * Checks if the current build is the Capacitor app target (`NUXT_PUBLIC_TARGET=app`).
 *
 * This is the build-time target — the app build only ships the /dashboard routes and is
 * always the installed/native app — resolved at runtime from public runtime config. Distinct
 * from isPWAInstalled(), which detects an installed PWA/native display mode at runtime.
 *
 * @returns {boolean} - Returns true when running the app build.
 */
export const isApp = () => useRuntimeConfig().public.target === 'app'

/*
 * Checks if the is in TWA (Android Wrapper) Mode
 *
 * @returns {boolean} - Returns true if the PWA is installed, otherwise false.
 */
export const isTWA = () => {
  const { $appDeviceInfo } = useNuxtApp()
  return $appDeviceInfo?.displayMode === 'twa'
}

/*
 * Generates the login page URL for the current runtime.
 *
 * Returns the `/dashboard/*` (PWA) auth route when running the Capacitor app build (isApp)
 * or an installed web PWA (isPWAInstalled); otherwise the standard web route. The app build
 * only ships `/dashboard/**` routes, so the web `/login/` route does not exist there — using
 * isApp() here prevents redirecting to a stripped route (which loops back to /dashboard).
 *
 * @returns {string} - The login page URL.
 */
export const generateLoginPageUrl = () => {
  return (isApp() || isPWAInstalled()) ? memberhubConstants.AUTH_ROUTES_SET.PWA_LOGIN : memberhubConstants.AUTH_ROUTES_SET.LOGIN
}

export const generateForgotPasswordPageUrl = () => {
  return (isApp() || isPWAInstalled()) ? memberhubConstants.AUTH_ROUTES_SET.PWA_FORGOT_PASSWORD : memberhubConstants.AUTH_ROUTES_SET.FORGOT_PASSWORD
}
export const generateResetPasswordPageUrl = () => {
  return (isApp() || isPWAInstalled()) ? memberhubConstants.AUTH_ROUTES_SET.PWA_RESET_PASSWORD : memberhubConstants.AUTH_ROUTES_SET.RESET_PASSWORD
}
export const generateRegisterPageUrl = () => {
  return (isApp() || isPWAInstalled()) ? memberhubConstants.AUTH_ROUTES_SET.PWA_REGISTER : memberhubConstants.AUTH_ROUTES_SET.REGISTER
}
export const generateGuestWelcomeBackPageUrl = () => {
  return (isApp() || isPWAInstalled()) ? memberhubConstants.AUTH_ROUTES_SET.PWA_GUEST_WELCOME_BACK : memberhubConstants.AUTH_ROUTES_SET.GUEST_WELCOME_BACK
}
export const generateLinkExpiredPageUrl = () => {
  return (isApp() || isPWAInstalled()) ? memberhubConstants.AUTH_ROUTES_SET.PWA_LINK_EXPIRED : memberhubConstants.AUTH_ROUTES_SET.LINK_EXPIRED
}

export const readAuthSession = stateKey => {
  if (!import.meta.client || !stateKey) return {
    sourcePageUrl: '',
    ssoUrl: '',
    sourcePagePath: '',
    ssoPath: '',
    sourcePageQuery: {}
  }
  const raw = sessionStorage.getItem(stateKey)
  if (!raw) return {
    sourcePageUrl: '',
    ssoUrl: '',
    sourcePagePath: '',
    ssoPath: '',
    sourcePageQuery: {}
  }

  let sessionData = {}
  try {
    sessionData = JSON.parse(raw) || {}
  } catch {
    sessionData = {}
  }

  const {
    source_page_url: sourcePageUrl = '',
    sso_url: ssoUrl = '',
    source_page_path: sourcePagePath = '',
    sso_path: ssoPath = '',
    query = {},
    utms = {}
  } = sessionData

  const sourcePageQuery = { ...(query || {}), ...(utms || {}) }
  return { sourcePageUrl, ssoUrl, sourcePagePath, ssoPath, sourcePageQuery }
}

export const safeParseUrl = urlString => {
  if (!urlString || !urlString.trim()) return null
  try {
    return new URL(urlString)
  } catch {
    return null
  }
}

export const getRedirectionPath = ({ sourcePageUrl, authRoutes = [] }) => {
  const parts = safeParseUrl(sourcePageUrl)
  const currentPath = parts?.pathname ? trailingSlash(parts.pathname) : null
  if (currentPath && authRoutes.includes(currentPath)) {
    return '/dashboard/'
  }
  return currentPath || '/dashboard/'
}

export const applyPostLoginSideEffects = ({ tracking, profileData, stateKey, trackingContext }) => {
  if (import.meta.client) {
    sessionStorage.setItem('user_logged_in', 'true')
    if (stateKey) sessionStorage.setItem('social_auth', 'true')
    tracking.login({
      emailHashed: profileData.hash_id,
      ...trackingContext
    })
    if (stateKey) sessionStorage.removeItem(stateKey)
  }
}

/**
   * Generates a URL for an avatar image using the DiceBear API.
   *
   * @param {string} string - The seed string used to generate the avatar.
   * @param {number} [size=32] - The size of the avatar image in pixels.
   * @returns {string} - The generated avatar URL.
   */
export const generateAvatarUrl = (string, size = 32) => {
  return `https://api.dicebear.com/7.x/initials/svg?seed=${string}&size=${size}&backgroundColor=E8EBEF&textColor=000000&fontSize=45`
}

/**
   * Provides a fallback mechanism for profile images.
   *
   * If the profile image fails to load, this function sets the image source
   * to a generated avatar URL and updates the alt attribute.
   *
   * @param {Event} e - The event triggered when the image fails to load.
   * @param {string} string - The seed string used to generate the fallback avatar.
   * @param {number} [size=40] - The size of the fallback avatar image in pixels.
   */
export const profileImgFallback = (e, string, size = 40) => {
  e.target.src = generateAvatarUrl(string, size)
  e.target.onerror = null
  e.target.alt = string
}

/*
   * Constructs a formatted address string from an address object.
   *
   * This function takes an `addressObj` containing various address components
   * and builds a single formatted string. It includes apartment number, street details,
   * and suburb/state/postcode information, separated by commas.
   *
   * @param {Object} addressObj - The object containing address components.
   * @param {string} [addressObj.apartmentNumber] - The apartment number (optional).
   * @param {string} [addressObj.streetNumber] - The street number (optional).
   * @param {string} [addressObj.streetNumberSuffix] - The suffix for the street number (optional).
   * @param {string} [addressObj.streetName] - The name of the street (optional).
   * @param {string} [addressObj.streetSuffix] - The suffix for the street name (optional).
   * @param {string} [addressObj.streetType] - The type of the street (e.g., Avenue, Road) (optional).
   * @param {string} [addressObj.suburb] - The suburb name (optional).
   * @param {string} [addressObj.state] - The state name (optional).
   * @param {string} [addressObj.postCode] - The postal code (optional).
   * @returns {string} - A formatted address string.
   */
export const getAddressString = addressObj => {
  const addressArray = []
  if(addressObj?.apartmentNumber) {
    addressArray.push(addressObj?.apartmentNumber)
  }
  const street = [
    addressObj?.streetNumber,
    addressObj?.streetNumberSuffix,
    addressObj?.streetName,
    addressObj?.streetSuffix,
    addressObj?.streetType
  ].filter(Boolean).join(' ')

  if(street.trim().length) {
    addressArray.push(street)
  }

  const address = [
    addressObj?.suburb,
    addressObj?.state,
    addressObj?.postCode
  ].filter(Boolean).join(' ')

  if(address.trim().length) {
    addressArray.push(address)
  }

  return addressArray.join(', ')
}

/*
 * Checks if a value is "number-like".
 *
 * A value is considered "number-like" if:
 * - It is not `null`.
 * - It is not an empty string (`''`).
 * - It is not `NaN` (Not-a-Number).
 * - It is a finite number (not `Infinity` or `-Infinity`).
 *
 * @param {*} val - The value to check.
 * @returns {boolean} - Returns `true` if the value is "number-like", otherwise `false`.
 */
export const isNumberLike = val => {
  return val !== null &&
        val !== '' &&
        !isNaN(val) &&
        isFinite(val)
}

export const parseHtmlList = htmlString => {
  const parser = new DOMParser()
  const doc = parser.parseFromString(htmlString, 'text/html')
  return Array.from(doc.querySelectorAll('li')).map(li => li.textContent.trim())
}

/**
 * Prepends the internal domain to a relative URL if running in Capacitor.
 * @param {string} url - The URL to check and possibly prepend.
 * @param {string} [domainForPrepend] - The domain to prepend for internal links. Defaults to runtime config.
 * @returns {string} - The absolute URL for use in Capacitor, or the original URL otherwise.
 */
export function prependHostIfNeeded (url, domainForPrepend) {
  // Only prepend domain for Capacitor native apps
  // Skip during SSR (server-side) and web browsers
  if (typeof window === 'undefined' || !Capacitor?.isNativePlatform?.()) {
    return url // Return relative URL for SSR, local dev, and web
  }

  if (!domainForPrepend) {
    try {
      domainForPrepend = useRuntimeConfig().public.internalDomainForInlinesHyperlink
    } catch {
      domainForPrepend = ''
    }
  }
  if (url && !/^https?:\/\//i.test(url)) {
    return domainForPrepend + url
  }
  return url
}

export function formatDate (date, currentDateFormat = 'DD/MM/YYYY', format = 'DD/MM/YYYY') {
  if (!date) {
    return ''
  }

  if (currentDateFormat) {
    return dayjs(date,currentDateFormat).format(format)
  }

  return dayjs(date).format(format)
}

/**
 * Auto formats age field by allowing only numbers, commas and space.
 * @param $event {Event}
 */
export const handleInputAgeByComma = $event => {
  $event.target.value = $event.target.value
    .replace(/[^\d,]/g, '')
    .replace(/\b0+(\d)/g, '$1')
    .replace(/\s+/g, ' ')
    .replace(/,([^ ])/g, ', $1')
    .replace(/,(\s*,)+/g, ', ')
    .trim()
}

/**
 * Checks if a given date is before today
 * or before compare date
 * @param date {Date | String}
 * @param compareDate {Date | String}
 * @return Boolean
 */
export const disablePreviousDate = (date, { compareDate = null } = {}) => {
  date = dayjs(date)

  compareDate = !_isEmpty(compareDate)
    ? dayjs(compareDate, 'DD/MM/YYYY', true)
    : null

  if (compareDate) {
    return date.isBefore(compareDate, 'day')
  }

  return date.isBefore(dayjs(), 'day')
}

/**
 * Returns current age based on given date
 *
 * @param date {Date | String}
 * @return Number
 */
export const getAge = (date, format = 'DD/MM/YYYY') => {
  const now = dayjs()
  const birthDate = dayjs(date, format)

  if (!birthDate.isValid()) {
    return null
  }

  return now.diff(birthDate, 'year')
}

export const isWeekday = date => {
  const day = dayjs(date).day()
  return day !== 0 && day !== 6
}

export const addWorkingDays = (date, n) => {
  let d = dayjs(date)
  let added = 0
  while (added < n) {
    d = d.add(1, 'day')
    if (isWeekday(d)) added++
  }
  return d
}

export const UCA_MIN_WORKING_DAYS = 10

/**
 * HI-1550: the earliest policy start / first payment date an unsolicited (UCA) customer may
 * choose. The rule is strictly MORE THAN 10 working days from the submission date, so the
 * buffer day itself is not selectable and the floor is the next working day after it - the
 * eleventh working day. The agent-selected dates go stale while the customer sits on the quote
 * link, so the floor is recalculated on every load.
 *
 * Weekends are skipped; public holidays are not considered, which matches the backend rule in
 * AmsBundle\Manager\ValidationManager::verifyUnsolicitedPolicyAndPaymentDates().
 */
export const getUcaMinDate = (from = dayjs()) =>
  addWorkingDays(dayjs(from).startOf('day'), UCA_MIN_WORKING_DAYS + 1)

/**
 * Performs a deep clone of an object or array, preserving function references.
 *
 * Unlike `structuredClone`, this handles objects containing functions (e.g. component
 * definitions, validator callbacks) by copying them by reference rather than throwing.
 *
 * - Primitives and `null` are returned as-is.
 * - Arrays are recursively cloned element by element.
 * - Plain objects are recursively cloned property by property.
 * - Functions are copied by reference (not cloned).
 *
 * @param {*} obj - The value to deep clone.
 * @returns {*} A deep clone of the input, with functions preserved by reference.
 *
 * @example
 * const clone = deepClone(FORM_QUESTIONS)
 * clone.hasConcession.question = 'New question' // does not affect FORM_QUESTIONS
 */
export function deepClone (obj) {
  if (obj === null || typeof obj !== 'object') return obj
  if (Array.isArray(obj)) return obj.map(deepClone)
  return Object.fromEntries(
    Object.entries(obj).map(([k, v]) => [k, typeof v === 'function' ? v : deepClone(v)])
  )
}
